/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable indent */
import i18n from 'i18n';
import { messagesRequest } from 'modules/messages/actions';
import { push } from 'react-router-redux';
import { all, put, takeLatest } from 'redux-saga/effects';
import { isRequestOK } from 'utils/helpers';
import {
  authenticateFulfilled,
  authenticateMFAFulfilled,
  authenticateMFARejected,
  authenticateMFARequest,
  authenticateRejected,
  authenticateRequest,
  forgotPasswordRequest,
  forgotPasswordRequestFulfilled,
  forgotPasswordRequestRejected,
  mfaChangeRegionFulfilled,
  registerPasswordFulfilled,
  registerPasswordRejected,
  registerPasswordRequest,
  authCheckRestrictionsFulfilled,
  authCheckRestrictionsRejected,
  authCheckRestrictionsRequest,
  trustedLoginRequest,
  trustedLoginFulfilled,
  trustedLoginRejected,
} from './actions';
import endpoints, { api } from './endpoints';
import { AuthenticateResponse } from './interfaces';
import {
  AuthenticateMFARequestPayload,
  AuthenticateRequestPayload,
  ForgotPasswordRequestProps,
  RegisterPasswordRequestProps,
} from './sagaInterfaces';
import { validateTrustedToken } from './utils';

const LOGGED_REGION_KEY = 'logged_region';

function* authenticateRequestSaga({ payload }: AuthenticateRequestPayload) {
  const { callback, ...props } = payload;

  try {
    const { status, data }: AuthenticateResponse = yield api.post({
      url: endpoints.login(),
      data: props,
    });

    // eslint-disable-next-line no-unused-expressions
    callback && callback(data);

    if (data?.available_regions) {
      const region = data?.available_regions.find(
        region => region.code === data?.logged_region
      );
      localStorage.setItem(LOGGED_REGION_KEY, JSON.stringify(region));
    }

    if (payload.isChangeRegion) {
      return yield all([put(mfaChangeRegionFulfilled(data))]);
    }

    return yield isRequestOK(status)
      ? all([
          put(authenticateFulfilled(data)),
          data?.mfa?.required && put(push('/authenticate/mfa')),
        ])
      : all([put(authenticateRejected())]);
  } catch (error) {
    return yield put(authenticateRejected());
  }
}

function* authCheckRestrictionsRequestSaga() {
  try {
    const { data } = yield api.get({
      url: endpoints.checkRestrictions(),
    });

    return yield put(authCheckRestrictionsFulfilled(data));
  } catch (e) {
    return yield put(authCheckRestrictionsRejected());
  }
}

function* authenticateMFARequestSaga({
  payload,
}: AuthenticateMFARequestPayload) {
  const { callback, ...props } = payload;

  try {
    const { status, data }: AuthenticateResponse = yield api.post({
      url: endpoints.mfa(),
      data: props,
    });

    // eslint-disable-next-line no-unused-expressions
    callback && callback(data);

    return yield isRequestOK(status)
      ? all([put(authenticateMFAFulfilled(data)), put(push('/'))])
      : all([put(authenticateMFARejected())]);
  } catch (error) {
    return yield put(authenticateMFARejected());
  }
}

function* forgotPasswordRequestSaga({ payload }: ForgotPasswordRequestProps) {
  try {
    const { status } = yield api.post({
      url: endpoints.resetPassword(),
      data: payload,
    });

    return yield isRequestOK(status)
      ? all([
          put(forgotPasswordRequestFulfilled()),
          put(
            messagesRequest({
              type: 'success',
              message: i18n.t('EMAIL_RECOVER_KEY'),
            })
          ),
        ])
      : all([put(forgotPasswordRequestRejected())]);
  } catch (responseWithError) {
    return yield responseWithError &&
    responseWithError.response &&
    responseWithError.response.status === 452
      ? put(forgotPasswordRequestRejected())
      : all([put(forgotPasswordRequestRejected())]);
  }
}

function* registerPasswordRequestSaga({
  payload,
}: RegisterPasswordRequestProps) {
  try {
    const { status } = yield api.post({
      url: endpoints.update(),
      data: payload,
    });

    return yield isRequestOK(status)
      ? all([
          put(registerPasswordFulfilled(payload)),
          put(
            messagesRequest({
              type: 'success',
              message: i18n.t('PASSWORD_CHANGE_KEY'),
            })
          ),
        ])
      : all([put(registerPasswordRejected())]);
  } catch (responseWithError) {
    return yield put(registerPasswordRejected());
  }
}

function* trustedLoginRequestSaga({ payload }: { payload: string }) {
  try {
    const isTokenValid = validateTrustedToken(payload);

    if (!isTokenValid) {
      return yield all([put(trustedLoginRejected()), put(push('/'))]);
    }

    const { status, data } = yield api.post({
      url: endpoints.trustedLogin(),
      data: { token: payload },
    });

    return yield isRequestOK(status)
      ? all([
          put(trustedLoginFulfilled(payload)),
          put(authenticateFulfilled(data)),
        ])
      : all([put(trustedLoginRejected()), put(push('/'))]);
  } catch (responseWithError) {
    return yield all([put(trustedLoginRejected()), put(push('/'))]);
  }
}

export default [
  takeLatest(authenticateRequest, authenticateRequestSaga),
  takeLatest(forgotPasswordRequest, forgotPasswordRequestSaga),
  takeLatest(authenticateMFARequest, authenticateMFARequestSaga),
  takeLatest(registerPasswordRequest, registerPasswordRequestSaga),
  takeLatest(authCheckRestrictionsRequest, authCheckRestrictionsRequestSaga),
  takeLatest(trustedLoginRequest, trustedLoginRequestSaga),
];
